// ManiDine Restaurant App / src/pages/MenuManager.jsx / Updated: 2026-04-02 16:40 MDT

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import ManagerShell from "../components/ManagerShell";
import { db, storage } from "../firebase";
import useRestaurantContext from "../hooks/useRestaurantContext";
import { syncMenuCategoriesFromItems } from "../utils/syncMenuCategoriesFromItems";
import { parseMenuCsv, MENU_CSV_TEMPLATE } from "../utils/menuCsvParser";
import "../Dashboard.css";
import "../MenuManager.css";

const NAV_TABS = ["overview", "items", "categories"];

const emptyForm = {
  name: "",
  description: "",
  category: "",
  price: "",
  taxRate: "",
  status: "available",
  sellOnItsOwn: true,
  isAvailable: true,
  categoryId: "",
  itemType: "single", // "single" | "combo"
};

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

const makeEmptyComboRow = () => ({
  id: makeId(),
  name: "",
  qty: 1,
});

// ✅ NEW: Option-group (Uber-style) builders
const makeEmptyOption = () => ({
  id: makeId(),
  name: "",
  priceDelta: 0,
});

const makeEmptyGroup = () => ({
  id: makeId(),
  title: "",
  // Default: required group (choose 1)
  required: true,
  optional: false, // ✅ NEW: explicitly store optional/required
  minSelect: 1,
  maxSelect: 1,
  options: [makeEmptyOption()],
});

// IMPORTANT: normalize only for display/listing; editor state uses comboDraft
const normalizeComboItems = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x, idx) => ({
      id: `${Date.now()}_${idx}_${Math.random().toString(16).slice(2)}`,
      name: (x?.name || "").toString(),
      qty:
        typeof x?.qty === "number" ? x.qty : Number.parseInt(x?.qty, 10) || 1,
    }))
    .filter((x) => x.name.trim().length > 0);
};

// ✅ UPDATED: normalize comboGroups (Uber-style) for listing/initial edit load
// Supports both `required` and `optional` fields.
const normalizeComboGroups = (raw) => {
  if (!Array.isArray(raw)) return [];
  const groups = raw
    .map((g) => {
      const id = (g?.id || makeId()).toString();
      const title = (g?.title || "").toString();

      const optional = g?.optional === true || g?.isOptional === true;
      const required =
        optional ? false : g?.required !== false && g?.isRequired !== false;

      const minSelect =
        typeof g?.minSelect === "number"
          ? g.minSelect
          : Number.parseInt(g?.minSelect, 10);

      const maxSelect =
        typeof g?.maxSelect === "number"
          ? g.maxSelect
          : Number.parseInt(g?.maxSelect, 10);

      const safeMin = Number.isFinite(minSelect)
        ? Math.max(0, minSelect)
        : optional
        ? 0
        : 1;

      const safeMax = Number.isFinite(maxSelect)
        ? Math.max(1, maxSelect)
        : 1;

      const optionsRaw = Array.isArray(g?.options) ? g.options : [];
      const options = optionsRaw
        .map((o) => ({
          id: (o?.id || makeId()).toString(),
          name: (o?.name || "").toString(),
          priceDelta:
            typeof o?.priceDelta === "number"
              ? o.priceDelta
              : Number.parseFloat(o?.priceDelta) || 0,
        }))
        .filter((o) => o.name.trim().length > 0);

      return {
        id,
        title,
        required,
        optional,
        minSelect: required ? Math.max(1, safeMin || 1) : 0,
        maxSelect: required
          ? Math.max(1, Math.max(safeMin || 1, safeMax || 1))
          : Math.max(1, safeMax || 1),
        options,
      };
    })
    .filter((g) => g.title.trim().length > 0 || (g.options || []).length > 0);

  return groups;
};

const summarizeComboGroups = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) return "";
  // Example: "Size (Required, choose 1) • Add drinks (Optional)"
  return groups
    .map((g) => {
      const title = (g?.title || "").toString().trim() || "Group";
      const optional = g?.optional === true;
      const required = optional ? false : g?.required !== false;
      const min = Number.isFinite(g?.minSelect) ? g.minSelect : 0;
      const max = Number.isFinite(g?.maxSelect) ? g.maxSelect : 0;

      let chooseText = "";
      if (required) {
        if (min === 1 && max === 1) chooseText = "choose 1";
        else if (min > 0 && max > 0 && min === max) chooseText = `choose ${min}`;
        else if (min > 0 && max > 0) chooseText = `choose ${min}–${max}`;
        else if (min > 0) chooseText = `choose at least ${min}`;
        else chooseText = "required";
        return `${title} (Required, ${chooseText})`;
      } else {
        return `${title} (Optional)`;
      }
    })
    .join(" • ");
};


const MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE = 1200;
const MENU_IMAGE_EDITOR_DEFAULT_QUALITY = 0.82;
const MENU_IMAGE_EDITOR_MAX_INPUT_SIZE = 12 * 1024 * 1024;
const MENU_IMAGE_EDITOR_ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the selected image."));
    reader.readAsDataURL(file);
  });

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load the selected image."));
    img.src = src;
  });

const getSquareCropSettings = (img, zoom = 1, offsetX = 0, offsetY = 0) => {
  const safeZoom = Math.max(1, Number(zoom) || 1);
  const baseCropSide = Math.min(img.width, img.height);
  const cropSide = clamp(baseCropSide / safeZoom, 1, baseCropSide);

  const maxOffsetX = Math.max(0, (img.width - cropSide) / 2);
  const maxOffsetY = Math.max(0, (img.height - cropSide) / 2);

  const safeOffsetX = clamp(Number(offsetX) || 0, -maxOffsetX, maxOffsetX);
  const safeOffsetY = clamp(Number(offsetY) || 0, -maxOffsetY, maxOffsetY);

  const sx = clamp((img.width - cropSide) / 2 + safeOffsetX, 0, img.width - cropSide);
  const sy = clamp((img.height - cropSide) / 2 + safeOffsetY, 0, img.height - cropSide);

  return {
    cropSide,
    sx,
    sy,
    safeOffsetX,
    safeOffsetY,
    maxOffsetX,
    maxOffsetY,
  };
};

const drawSquareImageToCanvas = (canvas, img, { zoom, offsetX, offsetY, outputSize }) => {
  if (!canvas || !img) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const size = Math.max(200, Number(outputSize) || MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE);
  const { cropSide, sx, sy } = getSquareCropSettings(img, zoom, offsetX, offsetY);

  canvas.width = size;
  canvas.height = size;

  ctx.clearRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, size, size);
};

const buildOptimizedMenuImageFile = async ({
  file,
  src,
  zoom,
  offsetX,
  offsetY,
  outputSize,
  quality,
}) => {
  const img = await loadImageElement(src);
  const canvas = document.createElement("canvas");
  drawSquareImageToCanvas(canvas, img, {
    zoom,
    offsetX,
    offsetY,
    outputSize,
  });

  const safeQuality = clamp(Number(quality) || MENU_IMAGE_EDITOR_DEFAULT_QUALITY, 0.6, 0.95);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (value) => {
        if (value) resolve(value);
        else reject(new Error("Could not optimize the selected image."));
      },
      "image/jpeg",
      safeQuality
    );
  });

  const baseName = (file?.name || "menu-item")
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "");

  return new File([blob], `${baseName || "menu-item"}-optimized.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const MenuManager = () => {
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemError, setItemError] = useState("");
  const [draft, setDraft] = useState(() => ({ ...emptyForm }));
  const [editingId, setEditingId] = useState(null);
  const [itemSaving, setItemSaving] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");
  const [categoriesSuccess, setCategoriesSuccess] = useState("");
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryForm, setCategoryForm] = useState({
    id: null,
    name: "",
    sortOrder: 1,
    isActive: true,
  });
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryOrderDraft, setCategoryOrderDraft] = useState([]);
  const [categoryOrderDirty, setCategoryOrderDirty] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(new Set());
  const [categoryOrderSaving, setCategoryOrderSaving] = useState(false);
  const [syncingCategories, setSyncingCategories] = useState(false);
  const [syncSummary, setSyncSummary] = useState("");

  // ✅ CSV menu import state (add new items + update existing matching items)
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvPreviewItems, setCsvPreviewItems] = useState([]);
  const [csvPreviewErrors, setCsvPreviewErrors] = useState([]);
  const [csvImportMessage, setCsvImportMessage] = useState("");
  const [csvImportSaving, setCsvImportSaving] = useState(false);
  const csvFileInputRef = useRef(null);

  const [itemPhotoFile, setItemPhotoFile] = useState(null);
  const [itemPhotoPreviewUrl, setItemPhotoPreviewUrl] = useState("");
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [imageEditorBusy, setImageEditorBusy] = useState(false);
  const [imageEditorReady, setImageEditorReady] = useState(false);
  const [imageEditorSource, setImageEditorSource] = useState("");
  const [imageEditorOriginalFile, setImageEditorOriginalFile] = useState(null);
  const [imageEditorZoom, setImageEditorZoom] = useState(1);
  const [imageEditorOffsetX, setImageEditorOffsetX] = useState(0);
  const [imageEditorOffsetY, setImageEditorOffsetY] = useState(0);
  const [imageEditorOutputSize, setImageEditorOutputSize] = useState(
    MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE
  );
  const [imageEditorQuality, setImageEditorQuality] = useState(82);
  const imageEditorCanvasRef = useRef(null);
  const imageEditorImageRef = useRef(null);

  // ✅ Combos UI state
  const [itemTypeDraft, setItemTypeDraft] = useState("single"); // "single" | "combo"

  // Legacy fixed includes
  const [comboDraft, setComboDraft] = useState([makeEmptyComboRow()]);
  const comboEditsRef = useRef(new Map()); // Map<rowId, { name, qty }>

  // ✅ NEW: Uber-style option groups
  const [comboMode, setComboMode] = useState("groups"); // "groups" | "fixed"
  const [comboGroupsDraft, setComboGroupsDraft] = useState([makeEmptyGroup()]);

  // These refs hold text edits without re-rendering every keypress
  const groupEditsRef = useRef(new Map()); // Map<groupId, { title, required, optional, minSelect, maxSelect }>
  const optionEditsRef = useRef(new Map()); // Map<"groupId:optionId", { name, priceDelta }>

  const location = useLocation();
  const navigate = useNavigate();
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  // ✅ NEW: draft refs so Name/Description don’t wipe when comboDraft changes
  // This fixes: “typing in combo includes clears Name/Description”
  const nameDraftRef = useRef("");
  const descDraftRef = useRef("");

  const itemsRef = useMemo(
    () =>
      restaurantId
        ? collection(db, "restaurants", restaurantId, "menuItems")
        : null,
    [restaurantId]
  );
  const categoriesRef = useMemo(
    () =>
      restaurantId
        ? collection(db, "restaurants", restaurantId, "menuCategories")
        : null,
    [restaurantId]
  );

  const seedComboEdits = (rows) => {
    const map = new Map();
    (rows || []).forEach((r) => {
      map.set(r.id, {
        name: (r.name || "").toString(),
        qty:
          typeof r.qty === "number" ? r.qty : Number.parseInt(r.qty, 10) || 1,
      });
    });
    comboEditsRef.current = map;
  };

  const seedGroupEdits = (groups) => {
    const gMap = new Map();
    const oMap = new Map();

    (groups || []).forEach((g) => {
      gMap.set(g.id, {
        title: (g.title || "").toString(),
        required: g.required !== false,
        optional: g.optional === true,
        minSelect:
          typeof g.minSelect === "number"
            ? g.minSelect
            : Number.parseInt(g.minSelect, 10) || 0,
        maxSelect:
          typeof g.maxSelect === "number"
            ? g.maxSelect
            : Number.parseInt(g.maxSelect, 10) || 0,
      });

      (g.options || []).forEach((o) => {
        oMap.set(`${g.id}:${o.id}`, {
          name: (o.name || "").toString(),
          priceDelta:
            typeof o.priceDelta === "number"
              ? o.priceDelta
              : Number.parseFloat(o.priceDelta) || 0,
        });
      });
    });

    groupEditsRef.current = gMap;
    optionEditsRef.current = oMap;
  };

  useEffect(() => {
    setItems([]);
    setCategories([]);
    setItemsLoading(!!restaurantId);
    setCategoriesLoading(!!restaurantId);
    setItemError("");
    setCategoriesError("");
    setCategoriesSuccess("");
    setCsvImportOpen(false);
    setCsvFileName("");
    setCsvPreviewItems([]);
    setCsvPreviewErrors([]);
    setCsvImportMessage("");
    setCsvImportSaving(false);
    setEditingId(null);
    setCategoryOrderDirty(false);
    setCategoryOrderDraft([]);
    setItemTypeDraft("single");

    // reset legacy fixed combo
    const row = makeEmptyComboRow();
    setComboDraft([row]);
    seedComboEdits([row]);

    // reset groups combo
    const g = makeEmptyGroup();
    setComboGroupsDraft([g]);
    seedGroupEdits([g]);

    setComboMode("groups");
    setItemPhotoFile(null);
    setImageEditorOpen(false);
    setImageEditorBusy(false);
    setImageEditorReady(false);
    setImageEditorSource("");
    setImageEditorOriginalFile(null);
    setImageEditorZoom(1);
    setImageEditorOffsetX(0);
    setImageEditorOffsetY(0);
    setImageEditorOutputSize(MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE);
    setImageEditorQuality(82);
    imageEditorImageRef.current = null;

    // reset refs too
    nameDraftRef.current = "";
    descDraftRef.current = "";
  }, [restaurantId]);

  const activeTab = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const current = parts[1] || "overview"; // /menu/<tab>
    return NAV_TABS.includes(current) ? current : "overview";
  }, [location.pathname]);

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const current = parts[1];
    if (!current || !NAV_TABS.includes(current)) {
      navigate("/menu/overview", { replace: true });
    }
  }, [location.pathname, navigate]);


  useEffect(() => {
    if (!itemPhotoFile) {
      setItemPhotoPreviewUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(itemPhotoFile);
    setItemPhotoPreviewUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [itemPhotoFile]);

  useEffect(() => {
    if (!imageEditorOpen || !imageEditorSource) {
      imageEditorImageRef.current = null;
      setImageEditorReady(false);
      return undefined;
    }

    let cancelled = false;

    loadImageElement(imageEditorSource)
      .then((img) => {
        if (cancelled) return;
        imageEditorImageRef.current = img;
        setImageEditorReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Load editor image failed:", err);
        setItemError("Could not load the selected image.");
        setImageEditorOpen(false);
        setImageEditorSource("");
        setImageEditorOriginalFile(null);
        setImageEditorReady(false);
      });

    return () => {
      cancelled = true;
      imageEditorImageRef.current = null;
      setImageEditorReady(false);
    };
  }, [imageEditorOpen, imageEditorSource]);

  useEffect(() => {
    if (!imageEditorOpen || !imageEditorReady || !imageEditorCanvasRef.current || !imageEditorImageRef.current) {
      return;
    }

    const settings = getSquareCropSettings(
      imageEditorImageRef.current,
      imageEditorZoom,
      imageEditorOffsetX,
      imageEditorOffsetY
    );

    if (settings.safeOffsetX !== imageEditorOffsetX) {
      setImageEditorOffsetX(settings.safeOffsetX);
      return;
    }

    if (settings.safeOffsetY !== imageEditorOffsetY) {
      setImageEditorOffsetY(settings.safeOffsetY);
      return;
    }

    drawSquareImageToCanvas(imageEditorCanvasRef.current, imageEditorImageRef.current, {
      zoom: imageEditorZoom,
      offsetX: imageEditorOffsetX,
      offsetY: imageEditorOffsetY,
      outputSize: 360,
    });
  }, [
    imageEditorOpen,
    imageEditorReady,
    imageEditorZoom,
    imageEditorOffsetX,
    imageEditorOffsetY,
    imageEditorSource,
  ]);

  const closeImageEditor = () => {
    setImageEditorOpen(false);
    setImageEditorBusy(false);
    setImageEditorReady(false);
    setImageEditorSource("");
    setImageEditorOriginalFile(null);
    setImageEditorZoom(1);
    setImageEditorOffsetX(0);
    setImageEditorOffsetY(0);
    setImageEditorOutputSize(MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE);
    setImageEditorQuality(82);
    imageEditorImageRef.current = null;
  };

  const handleItemPhotoPick = async (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;

    const isAllowedType =
      MENU_IMAGE_EDITOR_ALLOWED_TYPES.includes(file.type) ||
      /\.(jpe?g|png|webp)$/i.test(file.name || "");

    if (!isAllowedType) {
      setItemError("Please upload a JPG, PNG, or WEBP image.");
      return;
    }

    if (file.size > MENU_IMAGE_EDITOR_MAX_INPUT_SIZE) {
      setItemError("Please choose an image smaller than 12MB before editing.");
      return;
    }

    try {
      setItemError("");
      const src = await readFileAsDataUrl(file);
      setImageEditorOriginalFile(file);
      setImageEditorSource(src);
      setImageEditorZoom(1);
      setImageEditorOffsetX(0);
      setImageEditorOffsetY(0);
      setImageEditorOutputSize(MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE);
      setImageEditorQuality(82);
      setImageEditorOpen(true);
    } catch (err) {
      console.error("Read image failed:", err);
      setItemError("Could not open the selected image.");
    }
  };

  const applyOptimizedItemPhoto = async () => {
    if (!imageEditorOriginalFile || !imageEditorSource) return;

    try {
      setImageEditorBusy(true);
      setItemError("");
      const optimizedFile = await buildOptimizedMenuImageFile({
        file: imageEditorOriginalFile,
        src: imageEditorSource,
        zoom: imageEditorZoom,
        offsetX: imageEditorOffsetX,
        offsetY: imageEditorOffsetY,
        outputSize: imageEditorOutputSize,
        quality: imageEditorQuality / 100,
      });
      setItemPhotoFile(optimizedFile);
      closeImageEditor();
    } catch (err) {
      console.error("Optimize image failed:", err);
      setItemError("Could not optimize the selected image.");
    } finally {
      setImageEditorBusy(false);
    }
  };

  const clearSelectedItemPhoto = () => {
    setItemPhotoFile(null);
    setItemError("");
  };

  useEffect(() => {
    if (restaurantLoading) {
      setCategoriesLoading(true);
      return () => {};
    }
    if (!categoriesRef) {
      setCategories([]);
      setCategoriesLoading(false);
      return () => {};
    }

    const q = query(
      categoriesRef,
      orderBy("sortOrder", "asc"),
      orderBy("name", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: data.name || "",
            sortOrder:
              typeof data.sortOrder === "number"
                ? data.sortOrder
                : Number.parseInt(data.sortOrder, 10) || 0,
            isActive: data.isActive !== false,
          };
        });
        setCategories(next);
        setCategoriesError("");
        setCategoriesLoading(false);
      },
      (err) => {
        console.error("Load categories failed:", err);
        setCategoriesError("Unable to load categories.");
        setCategoriesLoading(false);
      }
    );
    return () => unsub();
  }, [categoriesRef, restaurantLoading]);

  useEffect(() => {
    if (restaurantLoading) {
      setItemsLoading(true);
      return () => {};
    }
    if (!itemsRef) {
      setItems([]);
      setItemsLoading(false);
      return () => {};
    }

    const q = query(itemsRef, orderBy("name", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs
          .map((d) => {
            const data = d.data() || {};
            const statusRaw = data.status || "available";
            const price =
              typeof data.price === "number"
                ? data.price
                : Number.parseFloat(data.price) || 0;

            const itemType = data.itemType === "combo" ? "combo" : "single";
            const comboItems = normalizeComboItems(data.comboItems);
            const comboGroups = normalizeComboGroups(data.comboGroups);

            return {
              id: d.id,
              name: data.name || "",
              description: data.description || "",
              price,
              taxRate:
                typeof data.taxRate === "number"
                  ? data.taxRate
                  : Number.parseFloat(data.taxRate) || "",
              categoryId:
                typeof data.categoryId === "string" ? data.categoryId : "",
              category: data.category || "",
              status: statusRaw,
              sellOnItsOwn: data.sellOnItsOwn !== false,
              isAvailable:
                data.isAvailable !== undefined
                  ? data.isAvailable
                  : statusRaw === "available",
              imageUrl: typeof data.imageUrl === "string" ? data.imageUrl : "",
              itemType,
              comboItems,
              comboGroups,
            };
          })
          .filter((i) => i.name || i.description || i.price >= 0);

        setItems(next);
        setItemsLoading(false);
        setItemError("");
      },
      (err) => {
        console.error("Load items failed:", err);
        setItemError("Unable to load menu items.");
        setItemsLoading(false);
      }
    );
    return () => unsub();
  }, [itemsRef, restaurantLoading]);

  const sortCategoriesForOverview = (cats) =>
    cats.slice().sort((a, b) => {
      const diff = (a.sortOrder || 0) - (b.sortOrder || 0);
      if (diff !== 0) return diff;
      return (a.name || "").localeCompare(b.name || "");
    });

  useEffect(() => {
    setCategoryOrderDraft(sortCategoriesForOverview(categories));
    setCategoryOrderDirty(false);
  }, [categories]);

  useEffect(() => {
    if (categoriesSuccess) {
      const timer = setTimeout(() => setCategoriesSuccess(""), 2500);
      return () => clearTimeout(timer);
    }
  }, [categoriesSuccess]);

  useEffect(() => {
    if (categoryForm.id) return;
    setCategoryForm((prev) => ({
      ...prev,
      sortOrder: computeNextCategorySort(categories),
    }));
  }, [categories, categoryForm.id]);

  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);

  const categoryByName = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set((c.name || "").trim().toLowerCase(), c));
    return map;
  }, [categories]);

  const matchCategoryForItem = (item) => {
    if (item.categoryId && categoryById.has(item.categoryId)) {
      return categoryById.get(item.categoryId);
    }
    const nameKey = (item.category || "").trim().toLowerCase();
    if (nameKey && categoryByName.has(nameKey)) {
      return categoryByName.get(nameKey);
    }
    return null;
  };

  const getItemCountForCategory = React.useCallback(
    (category) => {
      if (!category) return 0;
      return items.reduce((count, item) => {
        const matched = matchCategoryForItem(item);
        return matched && matched.id === category.id ? count + 1 : count;
      }, 0);
    },
    [items, categoryById, categoryByName]
  );

  const categoriesWithItems = useMemo(() => {
    const mapped = categoryOrderDraft.map((cat) => ({
      ...cat,
      items: [],
    }));
    const uncategorized = [];
    items.forEach((item) => {
      const cat = matchCategoryForItem(item);
      if (cat) {
        const bucket = mapped.find((c) => c.id === cat.id);
        if (bucket) bucket.items.push(item);
      } else {
        uncategorized.push(item);
      }
    });
    return { mapped, uncategorized };
  }, [categoryOrderDraft, items]);

  const resetForm = () => {
    setDraft({ ...emptyForm });
    setEditingId(null);
    setItemTypeDraft("single");

    // legacy fixed
    const row = makeEmptyComboRow();
    setComboDraft([row]);
    seedComboEdits([row]);

    // groups
    const g = makeEmptyGroup();
    setComboGroupsDraft([g]);
    seedGroupEdits([g]);

    setComboMode("groups");
    setItemPhotoFile(null);
    closeImageEditor();

    // clear refs so new item starts blank
    nameDraftRef.current = "";
    descDraftRef.current = "";
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setDraft({
      name: item.name || "",
      description: item.description || "",
      category: item.category || "",
      price:
        typeof item.price === "number" ? item.price.toString() : item.price || "",
      taxRate:
        item.taxRate !== undefined && item.taxRate !== null
          ? item.taxRate.toString()
          : "",
      status: item.status || "available",
      sellOnItsOwn: item.sellOnItsOwn !== false,
      isAvailable: item.isAvailable ?? true,
      categoryId: item.categoryId || "",
      itemType: item.itemType === "combo" ? "combo" : "single",
    });

    // ✅ keep refs in sync so uncontrolled inputs won’t wipe
    nameDraftRef.current = item.name || "";
    descDraftRef.current = item.description || "";

    setItemTypeDraft(item.itemType === "combo" ? "combo" : "single");

    // Prefer groups if present
    const groups = normalizeComboGroups(item.comboGroups);
    if (groups.length > 0) {
      setComboMode("groups");
      const safeGroups = groups.map((g) => ({
        ...g,
        options: g.options?.length ? g.options : [makeEmptyOption()],
      }));
      setComboGroupsDraft(safeGroups);
      seedGroupEdits(safeGroups);
    } else {
      // fallback to legacy fixed includes
      setComboMode("fixed");
      const normalized = normalizeComboItems(item.comboItems);
      const rows = normalized.length ? normalized : [makeEmptyComboRow()];
      setComboDraft(rows);
      seedComboEdits(rows);
    }
  };

  // If items refresh while editing, keep state aligned
  useEffect(() => {
    if (!editingId) return;
    const current = items.find((it) => it.id === editingId);
    if (!current) return;

    setDraft({
      name: current.name || "",
      description: current.description || "",
      category: current.category || "",
      price:
        typeof current.price === "number"
          ? current.price.toString()
          : current.price || "",
      taxRate:
        current.taxRate !== undefined && current.taxRate !== null
          ? current.taxRate.toString()
          : "",
      status: current.status || "available",
      sellOnItsOwn: current.sellOnItsOwn !== false,
      isAvailable: current.isAvailable ?? true,
      categoryId: current.categoryId || "",
      itemType: current.itemType === "combo" ? "combo" : "single",
    });

    nameDraftRef.current = current.name || "";
    descDraftRef.current = current.description || "";

    setItemTypeDraft(current.itemType === "combo" ? "combo" : "single");

    const groups = normalizeComboGroups(current.comboGroups);
    if (groups.length > 0) {
      setComboMode("groups");
      const safeGroups = groups.map((g) => ({
        ...g,
        options: g.options?.length ? g.options : [makeEmptyOption()],
      }));
      setComboGroupsDraft(safeGroups);
      seedGroupEdits(safeGroups);
    } else {
      setComboMode("fixed");
      const normalized = normalizeComboItems(current.comboItems);
      const rows = normalized.length ? normalized : [makeEmptyComboRow()];
      setComboDraft(rows);
      seedComboEdits(rows);
    }
  }, [editingId, items]);

  useEffect(() => {
    setItemPhotoFile(null);
    closeImageEditor();
  }, [editingId]);

  const ensureCategoryForInput = async (inputValue, selectedId) => {
    if (!categoriesRef) throw new Error("Missing restaurantId for categories.");
    if (selectedId && categoryById.has(selectedId)) {
      const cat = categoryById.get(selectedId);
      return { id: cat.id, name: cat.name };
    }
    const trimmed = (inputValue || "").trim();
    if (!trimmed) return { id: null, name: "" };
    const existing = categoryByName.get(trimmed.toLowerCase());
    if (existing) return { id: existing.id, name: existing.name };
    const sortOrder =
      categories.length > 0
        ? Math.max(...categories.map((c) => c.sortOrder || 0)) + 1
        : 1;
    const newDoc = doc(categoriesRef);
    await setDoc(newDoc, {
      name: trimmed,
      sortOrder,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: newDoc.id, name: trimmed };
  };

  const handleCategoryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryForm((prev) => ({
      ...prev,
      [name]:
        name === "sortOrder"
          ? Number.parseInt(value, 10) || 1
          : type === "checkbox"
          ? checked
          : value,
    }));
  };

  const startCategoryEdit = (category) => {
    if (!category) return;
    setCategoriesError("");
    setCategoriesSuccess("");
    setCategoryForm({
      id: category.id,
      name: category.name || "",
      sortOrder:
        typeof category.sortOrder === "number"
          ? category.sortOrder
          : Number.parseInt(category.sortOrder, 10) || 1,
      isActive: category.isActive !== false,
    });
  };

  const resetCategoryFormState = () => {
    setCategoryForm({
      id: null,
      name: "",
      sortOrder: computeNextCategorySort(categories),
      isActive: true,
    });
  };

  // ✅ legacy fixed combo editor handlers
  const addComboRow = () =>
    setComboDraft((prev) => {
      const row = makeEmptyComboRow();
      comboEditsRef.current.set(row.id, { name: "", qty: 1 });
      return [...prev, row];
    });

  const removeComboRow = (rowId) =>
    setComboDraft((prev) => {
      comboEditsRef.current.delete(rowId);
      const next = prev.filter((r) => r.id !== rowId);
      if (next.length) return next;

      const row = makeEmptyComboRow();
      comboEditsRef.current.set(row.id, { name: "", qty: 1 });
      return [row];
    });

  const updateComboRow = (rowId, patch) =>
    setComboDraft((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );

  // ✅ NEW: comboGroups handlers (Uber-style)
  const addGroup = () =>
    setComboGroupsDraft((prev) => {
      const g = makeEmptyGroup();
      groupEditsRef.current.set(g.id, {
        title: "",
        required: true,
        optional: false,
        minSelect: 1,
        maxSelect: 1,
      });
      const firstOpt = g.options[0];
      optionEditsRef.current.set(`${g.id}:${firstOpt.id}`, {
        name: "",
        priceDelta: 0,
      });
      return [...prev, g];
    });

  const removeGroup = (groupId) =>
    setComboGroupsDraft((prev) => {
      const next = prev.filter((g) => g.id !== groupId);
      // cleanup refs
      groupEditsRef.current.delete(groupId);
      Array.from(optionEditsRef.current.keys()).forEach((k) => {
        if (k.startsWith(`${groupId}:`)) optionEditsRef.current.delete(k);
      });

      if (next.length) return next;

      // always keep at least 1 group
      const g = makeEmptyGroup();
      groupEditsRef.current.set(g.id, {
        title: "",
        required: true,
        optional: false,
        minSelect: 1,
        maxSelect: 1,
      });
      const firstOpt = g.options[0];
      optionEditsRef.current.set(`${g.id}:${firstOpt.id}`, {
        name: "",
        priceDelta: 0,
      });
      return [g];
    });

  const updateGroup = (groupId, patch) =>
    setComboGroupsDraft((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, ...patch } : g))
    );

  // ✅ NEW: when Required checkbox toggles, force min/max so Firebase reflects it
  const setGroupRequired = (groupId, nextRequired) => {
    const prev = groupEditsRef.current.get(groupId) || {
      title: "",
      required: true,
      optional: false,
      minSelect: 1,
      maxSelect: 1,
    };

    if (nextRequired) {
      const next = {
        ...prev,
        required: true,
        optional: false,
        minSelect: Math.max(1, Number.parseInt(prev.minSelect, 10) || 1),
        maxSelect: Math.max(1, Number.parseInt(prev.maxSelect, 10) || 1),
      };
      groupEditsRef.current.set(groupId, next);
      updateGroup(groupId, {
        required: true,
        optional: false,
        minSelect: next.minSelect,
        maxSelect: next.maxSelect,
      });
    } else {
      const next = {
        ...prev,
        required: false,
        optional: true,
        minSelect: 0,
        maxSelect: Math.max(1, Number.parseInt(prev.maxSelect, 10) || 1),
      };
      groupEditsRef.current.set(groupId, next);
      updateGroup(groupId, {
        required: false,
        optional: true,
        minSelect: 0,
        maxSelect: next.maxSelect,
      });
    }
  };

  const addOption = (groupId) =>
    setComboGroupsDraft((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const opt = makeEmptyOption();
        optionEditsRef.current.set(`${groupId}:${opt.id}`, {
          name: "",
          priceDelta: 0,
        });
        return { ...g, options: [...(g.options || []), opt] };
      })
    );

  const removeOption = (groupId, optionId) =>
    setComboGroupsDraft((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const nextOpts = (g.options || []).filter((o) => o.id !== optionId);
        optionEditsRef.current.delete(`${groupId}:${optionId}`);

        if (nextOpts.length) return { ...g, options: nextOpts };

        // keep at least one option
        const opt = makeEmptyOption();
        optionEditsRef.current.set(`${groupId}:${opt.id}`, {
          name: "",
          priceDelta: 0,
        });
        return { ...g, options: [opt] };
      })
    );

  const updateOption = (groupId, optionId, patch) =>
    setComboGroupsDraft((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          options: (g.options || []).map((o) =>
            o.id === optionId ? { ...o, ...patch } : o
          ),
        };
      })
    );

  const handleItemSave = async (e) => {
    e.preventDefault();
    setItemError("");

    if (restaurantLoading) {
      setItemError("Loading restaurant…");
      return;
    }
    if (!restaurantId || !itemsRef) {
      setItemError("No restaurant found. Please sign in again.");
      return;
    }

    const form = e.target;
    const formData = new FormData(form);

    // ✅ read from refs first (prevents losing typed values if DOM re-mounted)
    const name =
      (nameDraftRef.current || formData.get("name") || "").toString().trim();
    const description = (
      descDraftRef.current ||
      formData.get("description") ||
      ""
    )
      .toString()
      .trim();

    const categoryInput = (formData.get("category") || "").toString().trim();
    const priceStr = (formData.get("price") || "").toString().trim();
    const taxRateStr = (formData.get("taxRate") || "").toString().trim();
    const status = (formData.get("status") || "available").toString();
    const sellOnItsOwn = formData.get("sellOnItsOwn") === "on";

    const itemType = (formData.get("itemType") || itemTypeDraft || "single")
      .toString()
      .trim();
    const isCombo = itemType === "combo";

    const priceNum = Number.parseFloat(priceStr);

    if (!name) {
      setItemError("Name is required.");
      return;
    }
    if (Number.isNaN(priceNum)) {
      setItemError("Price is required.");
      return;
    }

    // Clean legacy fixed includes
    const cleanedComboItems = isCombo
      ? Array.from(comboEditsRef.current.entries())
          .map(([id, v]) => ({
            name: (v?.name || "").toString().trim(),
            qty:
              typeof v?.qty === "number"
                ? v.qty
                : Number.parseInt(v?.qty, 10) || 1,
          }))
          .filter((r) => r.name.length > 0 && r.qty >= 1)
      : [];

    // ✅ Clean Uber-style groups (writes optional/min/max for Customer app)
    const cleanedComboGroups = isCombo
      ? comboGroupsDraft
          .map((g) => {
            const gEdit = groupEditsRef.current.get(g.id) || {};
            const title = (gEdit.title ?? g.title ?? "").toString().trim();

            const required =
              gEdit.required !== undefined
                ? !!gEdit.required
                : g.required !== false;

            const optional =
              gEdit.optional !== undefined
                ? !!gEdit.optional
                : g.optional === true || !required;

            const minSelectRaw = gEdit.minSelect ?? g.minSelect ?? 0;
            const maxSelectRaw = gEdit.maxSelect ?? g.maxSelect ?? 1;

            const minSelectNum = Math.max(
              0,
              Number.parseInt(minSelectRaw, 10) || 0
            );
            const maxSelectNum = Math.max(
              1,
              Number.parseInt(maxSelectRaw, 10) || 1
            );

            const options = (g.options || [])
              .map((o) => {
                const key = `${g.id}:${o.id}`;
                const oEdit = optionEditsRef.current.get(key) || {};
                const name = (oEdit.name ?? o.name ?? "").toString().trim();
                const priceDelta =
                  typeof oEdit.priceDelta === "number"
                    ? oEdit.priceDelta
                    : Number.parseFloat(oEdit.priceDelta ?? o.priceDelta) || 0;

                return {
                  id: o.id,
                  name,
                  priceDelta,
                };
              })
              .filter((o) => o.name.length > 0);

            // keep group only if it has title + at least 1 option
            if (!title || options.length === 0) return null;

            // ✅ KEY RULE:
            // Optional => minSelect MUST be 0
            // Required => minSelect >= 1
            const safeMin = optional ? 0 : Math.max(1, minSelectNum || 1);
            const safeMax = optional
              ? Math.max(1, maxSelectNum || 1)
              : Math.max(safeMin, maxSelectNum || safeMin);

            return {
              id: g.id,
              title,
              required: !optional,
              optional: !!optional,
              minSelect: safeMin,
              maxSelect: safeMax,
              options,
            };
          })
          .filter(Boolean)
      : [];

    // Validation: if combo, must have at least one config
    if (isCombo) {
      if (comboMode === "groups" && cleanedComboGroups.length === 0) {
        setItemError(
          "For Combo (Option Groups), please add at least 1 group with at least 1 option."
        );
        return;
      }
      if (comboMode === "fixed" && cleanedComboItems.length === 0) {
        setItemError(
          "For a combo item, please add at least 1 included item (e.g., Burger, Fries, Drink)."
        );
        return;
      }
    }

    if (itemPhotoFile) {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
      const isAllowedType =
        allowedTypes.includes(itemPhotoFile.type) ||
        /\.(jpe?g|png|webp)$/i.test(itemPhotoFile.name || "");
      const isAllowedSize = itemPhotoFile.size <= 5 * 1024 * 1024;
      if (!isAllowedType) {
        setItemError("Please upload a JPG, PNG, or WEBP image.");
        return;
      }
      if (!isAllowedSize) {
        setItemError("Optimized image must be 5MB or smaller.");
        return;
      }
    }

    setItemSaving(true);

    try {
      const categoryResult = await ensureCategoryForInput(categoryInput, null);
      const finalStatus = status || "available";

      const targetRef = editingId ? doc(itemsRef, editingId) : doc(itemsRef);
      const itemId = targetRef.id;

      // Save BOTH fields for compatibility:
      // - comboGroups (new) when mode=groups
      // - comboItems (legacy) when mode=fixed
      const payload = {
        name,
        description,
        price: priceNum,
        taxRate: taxRateStr === "" ? null : Number.parseFloat(taxRateStr) || 0,
        category: categoryResult.name,
        categoryId: categoryResult.id,
        status: finalStatus,
        sellOnItsOwn: !!sellOnItsOwn,
        isAvailable: finalStatus === "available",
        restaurantId,
        updatedAt: serverTimestamp(),
        itemType: isCombo ? "combo" : "single",
        comboItems: isCombo && comboMode === "fixed" ? cleanedComboItems : [],
        comboGroups: isCombo && comboMode === "groups" ? cleanedComboGroups : [],
        comboMode: isCombo ? comboMode : "single",
      };

      if (!editingId) {
        payload.createdAt = serverTimestamp();
      }

      if (
        itemPhotoFile &&
        itemPhotoFile instanceof File &&
        itemPhotoFile.size > 0
      ) {
        const ext =
          itemPhotoFile.type === "image/png" ||
          (itemPhotoFile.name || "").toLowerCase().endsWith(".png")
            ? "png"
            : "jpg";
        const storageRef = ref(
          storage,
          `restaurant_items/${restaurantId}/${itemId}.${ext}`
        );
        await uploadBytes(storageRef, itemPhotoFile);
        const imageUrl = await getDownloadURL(storageRef);
        payload.imageUrl = imageUrl;
      }

      await setDoc(targetRef, payload, { merge: true });

      form.reset();
      setEditingId(null);
      setItemPhotoFile(null);
      setItemTypeDraft("single");

      // reset legacy fixed
      const row = makeEmptyComboRow();
      setComboDraft([row]);
      seedComboEdits([row]);

      // reset groups
      const g = makeEmptyGroup();
      setComboGroupsDraft([g]);
      seedGroupEdits([g]);

      setComboMode("groups");

      // clear refs after save
      nameDraftRef.current = "";
      descDraftRef.current = "";
    } catch (err) {
      console.error("Save item failed:", err);
      setItemError("Failed to save item.");
    } finally {
      setItemSaving(false);
    }
  };

  const handleItemDelete = async () => {
    if (!editingId) return;
    if (!window.confirm("Delete this item?")) return;
    try {
      if (!itemsRef) {
        setItemError("No restaurant found. Please sign in again.");
        return;
      }
      await deleteDoc(doc(itemsRef, editingId));
      resetForm();
    } catch (err) {
      console.error("Delete item failed:", err);
      setItemError("Failed to delete item.");
    }
  };

  const computeNextCategorySort = (cats) => {
    if (!cats.length) return 1;
    return Math.max(...cats.map((c) => c.sortOrder || 0)) + 1;
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setCategoriesError("");
    if (!categoryForm.name.trim()) {
      setCategoriesError("Category name is required.");
      return;
    }
    if (restaurantLoading) {
      setCategoriesError("Loading restaurant…");
      return;
    }
    if (!categoriesRef || !restaurantId) {
      setCategoriesError("No restaurant found. Please sign in again.");
      return;
    }
    setCategorySaving(true);
    try {
      const sortOrder =
        typeof categoryForm.sortOrder === "number"
          ? categoryForm.sortOrder
          : Number.parseInt(categoryForm.sortOrder, 10) || 1;

      const payload = {
        name: categoryForm.name.trim(),
        sortOrder,
        isActive: categoryForm.isActive !== false,
        updatedAt: serverTimestamp(),
      };

      if (categoryForm.id) {
        await setDoc(doc(categoriesRef, categoryForm.id), payload, {
          merge: true,
        });
      } else {
        await setDoc(doc(categoriesRef), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      setCategoryForm({
        id: null,
        name: "",
        sortOrder: Math.max(sortOrder + 1, computeNextCategorySort(categories)),
        isActive: true,
      });
      setCategoriesSuccess("Category saved.");
    } catch (err) {
      console.error("Save category failed:", err);
      setCategoriesError("Failed to save category.");
    } finally {
      setCategorySaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this category? Items that belong to it will be uncategorized."
      )
    ) {
      return;
    }

    try {
      if (!categoriesRef) {
        setCategoriesError("No restaurant found. Please sign in again.");
        return;
      }
      if (!itemsRef) {
        setCategoriesError("No restaurant found. Please sign in again.");
        return;
      }

      await deleteDoc(doc(categoriesRef, id));

      const affectedItems = items.filter((item) => {
        const matched = matchCategoryForItem(item);
        return matched && matched.id === id;
      });

      if (affectedItems.length) {
        const batch = writeBatch(db);
        affectedItems.forEach((item) => {
          batch.update(doc(itemsRef, item.id), {
            categoryId: "",
            category: "",
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      if (categoryForm.id === id) {
        resetCategoryFormState();
      }
      setCategoriesSuccess("Category deleted.");
    } catch (err) {
      console.error("Delete category failed:", err);
      setCategoriesError("Failed to delete category.");
    }
  };

  const handleSyncCategories = async () => {
    setSyncingCategories(true);
    setCategoriesError("");
    setSyncSummary("");
    try {
      if (!itemsRef || !categoriesRef) {
        throw new Error("No restaurant found. Please sign in again.");
      }
      const result = await syncMenuCategoriesFromItems({
        db,
        itemsRef,
        categoriesRef,
      });
      setSyncSummary(
        `Created ${result.created} categories, ${result.skipped} already existed (from ${result.totalNames} unique names).`
      );
    } catch (err) {
      console.error("Sync categories failed:", err);
      setCategoriesError("Failed to sync categories from items.");
    } finally {
      setSyncingCategories(false);
    }
  };

  const resetCsvImport = () => {
    setCsvFileName("");
    setCsvPreviewItems([]);
    setCsvPreviewErrors([]);
    setCsvImportMessage("");
    setCsvImportSaving(false);
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = "";
    }
  };

  const openCsvImport = () => {
    setItemError("");
    resetCsvImport();
    setCsvImportOpen(true);
  };

  const closeCsvImport = () => {
    if (csvImportSaving) return;
    setCsvImportOpen(false);
    resetCsvImport();
  };

  const downloadCsvTemplate = () => {
    const blob = new Blob([MENU_CSV_TEMPLATE], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ManiDine_Menu_Import_Template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvFilePick = async (e) => {
    const file = e.target.files?.[0] || null;
    setCsvImportMessage("");
    setCsvPreviewItems([]);
    setCsvPreviewErrors([]);

    if (!file) {
      setCsvFileName("");
      return;
    }

    const isCsv =
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel" ||
      /\.csv$/i.test(file.name || "");

    if (!isCsv) {
      setCsvFileName(file.name || "");
      setCsvPreviewErrors(["Please choose a CSV file exported from Google Sheets or Excel."]);
      return;
    }

    try {
      setCsvFileName(file.name || "Selected CSV");
      const csvText = await file.text();
      const parsed = parseMenuCsv(csvText);

      setCsvPreviewItems(parsed.items || []);
      setCsvPreviewErrors(parsed.errors || []);

      if ((parsed.items || []).length > 0 && (parsed.errors || []).length === 0) {
        const categoryCount = new Set(
          parsed.items.map((item) => (item.category || "").trim()).filter(Boolean)
        ).size;
        setCsvImportMessage(
          `Ready to add or update ${parsed.items.length} items across ${categoryCount} categories.`
        );
      } else if ((parsed.items || []).length > 0) {
        setCsvImportMessage(
          `${parsed.items.length} valid items found, but please fix the errors before importing.`
        );
      }
    } catch (err) {
      console.error("Read CSV failed:", err);
      setCsvPreviewErrors(["Could not read the selected CSV file."]);
    }
  };

  const resolveImportCategories = async (menuItems) => {
    if (!categoriesRef) {
      throw new Error("Missing restaurant categories collection.");
    }

    const categoryMap = new Map();
    categories.forEach((cat) => {
      const key = (cat.name || "").trim().toLowerCase();
      if (key) categoryMap.set(key, { id: cat.id, name: cat.name });
    });

    const uniqueNames = Array.from(
      new Set(
        menuItems
          .map((item) => (item.category || "").trim())
          .filter(Boolean)
      )
    );

    const missingNames = uniqueNames.filter(
      (name) => !categoryMap.has(name.toLowerCase())
    );

    if (missingNames.length === 0) {
      return categoryMap;
    }

    let batch = writeBatch(db);
    let batchCount = 0;
    const startSortOrder =
      categories.length > 0
        ? Math.max(...categories.map((c) => c.sortOrder || 0)) + 1
        : 1;

    for (let i = 0; i < missingNames.length; i += 1) {
      const name = missingNames[i];
      const newCategoryRef = doc(categoriesRef);
      const categoryPayload = {
        name,
        sortOrder: startSortOrder + i,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      batch.set(newCategoryRef, categoryPayload);
      batchCount += 1;
      categoryMap.set(name.toLowerCase(), { id: newCategoryRef.id, name });

      if (batchCount >= 450) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    return categoryMap;
  };

  const getCsvItemMatchKey = (name, category) =>
    `${(name || "").trim().toLowerCase()}::${(category || "").trim().toLowerCase()}`;

  const handleCsvImportSave = async () => {
    setCsvImportMessage("");
    setCsvPreviewErrors([]);

    if (restaurantLoading) {
      setCsvPreviewErrors(["Loading restaurant…"]);
      return;
    }
    if (!restaurantId || !itemsRef || !categoriesRef) {
      setCsvPreviewErrors(["No restaurant found. Please sign in again."]);
      return;
    }
    if (!csvPreviewItems.length) {
      setCsvPreviewErrors(["Please choose a valid CSV file before importing."]);
      return;
    }

    setCsvImportSaving(true);

    try {
      const categoryMap = await resolveImportCategories(csvPreviewItems);

      const existingItemsByKey = new Map();
      items.forEach((existingItem) => {
        const categoryName =
          (matchCategoryForItem(existingItem)?.name || existingItem.category || "").trim();
        const key = getCsvItemMatchKey(existingItem.name, categoryName);
        if (key && !existingItemsByKey.has(key)) {
          existingItemsByKey.set(key, existingItem);
        }
      });

      let batch = writeBatch(db);
      let batchCount = 0;
      let createdCount = 0;
      let updatedCount = 0;

      for (const item of csvPreviewItems) {
        const itemName = (item.name || "").trim();
        const categoryName = (item.category || "").trim();
        const categoryMatch = categoryMap.get(categoryName.toLowerCase()) || {
          id: "",
          name: categoryName,
        };

        const matchKey = getCsvItemMatchKey(itemName, categoryMatch.name || categoryName);
        const existingItem = existingItemsByKey.get(matchKey);
        const targetRef = existingItem?.id ? doc(itemsRef, existingItem.id) : doc(itemsRef);
        const isAvailable = item.isAvailable !== false;
        const status = isAvailable ? "available" : "out_of_stock";

        const payload = {
          name: itemName,
          description: (item.description || "").trim(),
          price: Number.parseFloat(item.price) || 0,
          taxRate:
            item.taxRate === "" || item.taxRate === undefined || item.taxRate === null
              ? null
              : Number.parseFloat(item.taxRate) || 0,
          category: categoryMatch.name || categoryName,
          categoryId: categoryMatch.id || "",
          status,
          sellOnItsOwn: item.sellOnItsOwn !== false,
          isAvailable,
          restaurantId,
          updatedAt: serverTimestamp(),
          itemType: existingItem?.itemType || "single",
          comboItems: existingItem?.comboItems || [],
          comboGroups: existingItem?.comboGroups || [],
          comboMode: existingItem?.comboMode || "single",
        };

        if (!existingItem) {
          payload.createdAt = serverTimestamp();
          payload.itemType = "single";
          payload.comboItems = [];
          payload.comboGroups = [];
          payload.comboMode = "single";
        }

        if (item.imageUrl) {
          payload.imageUrl = item.imageUrl;
        }

        batch.set(targetRef, payload, { merge: true });
        batchCount += 1;

        if (existingItem) updatedCount += 1;
        else createdCount += 1;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      setCsvImportMessage(
        `CSV import complete. Added ${createdCount} new item${createdCount === 1 ? "" : "s"} and updated ${updatedCount} existing item${updatedCount === 1 ? "" : "s"}.`
      );
      setCsvFileName("");
      setCsvPreviewItems([]);
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("CSV import failed:", err);
      setCsvPreviewErrors([
        err?.message || "Failed to import menu items. Please try again.",
      ]);
    } finally {
      setCsvImportSaving(false);
    }
  };

  const saveCategoryOrder = async () => {
    setCategoryOrderSaving(true);
    try {
      if (!categoriesRef) {
        setCategoriesError("No restaurant found.");
        setCategoryOrderSaving(false);
        return;
      }
      const batch = writeBatch(db);
      categoryOrderDraft.forEach((cat, idx) => {
        const newOrder = idx + 1;
        if (cat.sortOrder !== newOrder) {
          batch.update(doc(categoriesRef, cat.id), {
            sortOrder: newOrder,
            updatedAt: serverTimestamp(),
          });
        }
      });
      await batch.commit();
      setCategoryOrderDirty(false);
    } catch (err) {
      console.error("Save order failed:", err);
      setCategoriesError("Failed to save category order.");
    } finally {
      setCategoryOrderSaving(false);
    }
  };

  if (restaurantLoading) {
    return (
      <ManagerShell title="Menu manager" subtitle="Loading your restaurant...">
        <section className="menu-card">
          <h2 className="menu-section-title">Menu manager</h2>
          <p className="menu-microcopy">Loading restaurant…</p>
        </section>
      </ManagerShell>
    );
  }

  if (!restaurantId) {
    return (
      <ManagerShell title="Menu manager" subtitle="Please log in to manage your menu items.">
        <section className="menu-card">
          <h2 className="menu-section-title">Menu manager</h2>
          <p className="menu-microcopy">No restaurant found. Please sign in again.</p>
        </section>
      </ManagerShell>
    );
  }

  // --- CATEGORY EXPANSION STATE HELPERS ---
  const toggleCategory = (id) => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCategoryIds(
      new Set(
        (categoryOrderDraft.length ? categoryOrderDraft : categoriesWithItems.mapped).map((c) => c.id)
      )
    );
  };

  const collapseAll = () => {
    setExpandedCategoryIds(new Set());
  };

  const moveCategory = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= categoryOrderDraft.length) return;
    setCategoryOrderDraft((prev) => {
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
    setCategoryOrderDirty(true);
  };

  const renderOverviewTab = () => {
    const itemsByCategoryId = new Map();
    categoriesWithItems.mapped.forEach((cat) => {
      itemsByCategoryId.set(cat.id, cat.items || []);
    });

    const handleResetOrder = () => {
      setCategoryOrderDraft(sortCategoriesForOverview(categories));
      setCategoryOrderDirty(false);
    };

    const hasCategories = categoryOrderDraft.length > 0;

    return (
      <section className="menu-card menu-overview-card">
        <div className="menu-overview-header">
          <div className="menu-overview-header-left">
            <h2 className="menu-section-title">Menu</h2>
          </div>
          <div className="menu-overview-header-center">
            <button type="button" className="menu-link" onClick={expandAll}>
              Expand all
            </button>
            <button type="button" className="menu-link" onClick={collapseAll}>
              Collapse all
            </button>
          </div>
          <div className="menu-overview-header-right">
            <button
              type="button"
              className="menu-secondary-btn"
              onClick={handleResetOrder}
              disabled={!categories.length}
            >
              Reset order
            </button>
            <button
              type="button"
              className="menu-primary-btn"
              disabled={!categoryOrderDirty || categoryOrderSaving}
              onClick={saveCategoryOrder}
            >
              {categoryOrderSaving ? "Saving…" : "Save order"}
            </button>
          </div>
        </div>

        <div className="menu-microcopy menu-overview-helper">
          Use the arrows to reorder categories, then click Save order.
        </div>

        {!hasCategories && (
          <p className="menu-microcopy">
            No categories yet. Add items with categories in the Items tab.
          </p>
        )}

        {hasCategories && (
          <div className="menu-overview-list">
            {categoryOrderDraft.map((cat, index) => {
              const isExpanded = expandedCategoryIds.has(cat.id);
              const itemsInCat = itemsByCategoryId.get(cat.id) || [];
              const itemCount = itemsInCat.length;
              const alt = index % 2 === 1;

              return (
                <div
                  key={cat.id}
                  className={`menu-category-row ${alt ? "menu-category-row--alt" : ""}`}
                >
                  <div className="menu-category-left">
                    <span className="menu-category-handle" aria-hidden="true">
                      ≡
                    </span>
                    <div className="menu-category-move">
                      <button
                        type="button"
                        className="menu-category-move-btn"
                        onClick={() => moveCategory(index, -1)}
                        disabled={index === 0}
                        aria-label={`Move ${cat.name} up`}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="menu-category-move-btn"
                        onClick={() => moveCategory(index, 1)}
                        disabled={index === categoryOrderDraft.length - 1}
                        aria-label={`Move ${cat.name} down`}
                      >
                        ↓
                      </button>
                    </div>
                  </div>

                  <div className="menu-category-main">
                    <button
                      type="button"
                      className="menu-category-main-btn"
                      onClick={() => toggleCategory(cat.id)}
                    >
                      <div className="menu-category-name-wrap">
                        <span className="menu-category-chevron">
                          {isExpanded ? "▼" : "▶"}
                        </span>
                        <span className="menu-category-name">{cat.name || "Untitled"}</span>
                      </div>
                      <span className="menu-category-count-pill mobile-pill">
                        Items: {itemCount}
                      </span>
                    </button>

                    {isExpanded && itemsInCat.length > 0 && (
                      <div className="menu-category-items">
                        {itemsInCat.map((item) => {
                          const groupsSummary =
                            item.itemType === "combo" ? summarizeComboGroups(item.comboGroups) : "";

                          return (
                            <div key={item.id} className="menu-category-item-row">
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span className="menu-item-title">
                                  {item.name}
                                  {item.itemType === "combo" ? " (Combo)" : ""}
                                </span>

                                {item.itemType === "combo" && groupsSummary && (
                                  <div className="menu-item-sub">{groupsSummary}</div>
                                )}

                                {item.itemType === "combo" &&
                                  !groupsSummary &&
                                  Array.isArray(item.comboItems) &&
                                  item.comboItems.length > 0 && (
                                    <div className="menu-item-sub">
                                      Includes:{" "}
                                      {item.comboItems
                                        .map((x) =>
                                          x.qty && x.qty > 1 ? `${x.name} x${x.qty}` : x.name
                                        )
                                        .join(", ")}
                                    </div>
                                  )}
                              </div>

                              <div className="menu-overview-item-meta">
                                <span className="menu-item-price">
                                  {typeof item.price === "number" ? `$${item.price.toFixed(2)}` : "—"}
                                </span>
                                <span
                                  className={`menu-status-pill ${
                                    item.isAvailable || item.status === "available"
                                      ? "available"
                                      : "unavailable"
                                  }`}
                                >
                                  {item.isAvailable || item.status === "available"
                                    ? "Available"
                                    : "Out of stock"}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="menu-category-right">
                    <span className="menu-category-count-pill">Items: {itemCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderItemsTab = () => {
    const currentItem = editingId ? items.find((it) => it.id === editingId) || null : null;

    return (
      <div className="menu-layout">
        {/* LEFT: ITEMS TABLE */}
        <section className="menu-card">
          <div className="menu-card-heading">
            <div>
              <h2 className="menu-section-title">Items</h2>
              <p className="menu-microcopy">Manage all menu items with pricing and availability.</p>
            </div>
          </div>

          {itemError && <div className="menu-alert error">{itemError}</div>}

          <div className="menu-table-wrapper">
            <table className="menu-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {itemsLoading ? (
                  <tr>
                    <td colSpan={5} className="menu-microcopy">
                      Loading items…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="menu-microcopy">
                      No items yet.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const badgeLetter = (item.name || "·").charAt(0).toUpperCase();
                    const cat = matchCategoryForItem(item);
                    const groupsSummary =
                      item.itemType === "combo" ? summarizeComboGroups(item.comboGroups) : "";

                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="menu-item-cell">
                            {item.imageUrl ? (
                              <div className="menu-item-avatar menu-item-avatar--image">
                                <img src={item.imageUrl} alt={item.name || "Item image"} />
                              </div>
                            ) : (
                              <div className="menu-item-avatar">{badgeLetter}</div>
                            )}
                            <div>
                              <div className="menu-item-title">
                                {item.name}
                                {item.itemType === "combo" ? (
                                  <span style={{ marginLeft: 8, opacity: 0.7 }}>(Combo)</span>
                                ) : null}
                              </div>

                              {item.description && <div className="menu-item-sub">{item.description}</div>}

                              {/* ✅ Prefer showing option groups summary if exists */}
                              {item.itemType === "combo" && !!groupsSummary && (
                                <div className="menu-item-sub">{groupsSummary}</div>
                              )}

                              {/* Fallback legacy fixed includes */}
                              {item.itemType === "combo" &&
                                !groupsSummary &&
                                Array.isArray(item.comboItems) &&
                                item.comboItems.length > 0 && (
                                  <div className="menu-item-sub">
                                    Includes:{" "}
                                    {item.comboItems
                                      .map((x) => (x.qty && x.qty > 1 ? `${x.name} x${x.qty}` : x.name))
                                      .join(", ")}
                                  </div>
                                )}
                            </div>
                          </div>
                        </td>
                        <td>{cat ? cat.name : item.category || "Uncategorized"}</td>
                        <td>{typeof item.price === "number" ? `$${item.price.toFixed(2)}` : "—"}</td>
                        <td>
                          <span
                            className={`menu-status-pill ${
                              item.status === "archived"
                                ? "unavailable"
                                : item.isAvailable
                                ? "available"
                                : "unavailable"
                            }`}
                          >
                            {item.status === "archived"
                              ? "Archived"
                              : item.isAvailable
                              ? "Available"
                              : "Out of stock"}
                          </span>
                        </td>
                        <td className="menu-row-actions">
                          <button type="button" className="menu-link" onClick={() => handleEdit(item)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="menu-import-launch-card">
            <div>
              <h3 className="menu-import-launch-title">Bulk menu import</h3>
              <p className="menu-microcopy">
                Upload a CSV to add new items or update existing items that match by name and category.
              </p>
            </div>
            <div className="menu-import-actions">
              <button type="button" className="menu-secondary-btn" onClick={downloadCsvTemplate}>
                Download template
              </button>
              <button type="button" className="menu-primary-btn" onClick={openCsvImport}>
                Import CSV
              </button>
            </div>
          </div>
        </section>

        {/* RIGHT: EDITOR PANEL */}
        <section className="menu-card item-editor">
          <div className="menu-card-heading">
            <div>
              <h2 className="menu-section-title">{editingId ? "Edit item" : "New item"}</h2>
              <p className="menu-microcopy">Enter details for this item.</p>
            </div>
            {editingId && (
              <button type="button" className="menu-secondary-btn" onClick={resetForm}>
                Start new
              </button>
            )}
          </div>

          {itemError && <div className="menu-alert error">{itemError}</div>}

          {/* IMPORTANT: form is mostly uncontrolled */}
          <form className="menu-form" onSubmit={handleItemSave}>
            {/* NAME */}
            <label className="menu-field">
              <span>Name</span>
              <input
                name="name"
                autoComplete="off"
                defaultValue={currentItem ? currentItem.name : nameDraftRef.current}
                onInput={(e) => {
                  nameDraftRef.current = e.currentTarget.value;
                }}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="e.g., Turkey Breast"
                required
              />
            </label>

            {/* DESCRIPTION */}
            <label className="menu-field">
              <span>Description</span>
              <textarea
                name="description"
                rows={3}
                defaultValue={currentItem ? currentItem.description : descDraftRef.current}
                onInput={(e) => {
                  descDraftRef.current = e.currentTarget.value;
                }}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Short description for customers"
              />
            </label>

            {/* ITEM TYPE */}
            <label className="menu-field">
              <span>Item type</span>
              <select
                name="itemType"
                value={itemTypeDraft}
                onChange={(e) => {
                  const next = e.target.value === "combo" ? "combo" : "single";
                  setItemTypeDraft(next);
                  if (next === "combo") {
                    // Ensure at least one builder exists
                    if (comboMode === "fixed") {
                      setComboDraft((prev) => {
                        if (prev?.length) {
                          seedComboEdits(prev);
                          return prev;
                        }
                        const row = makeEmptyComboRow();
                        const rows = [row];
                        seedComboEdits(rows);
                        return rows;
                      });
                    } else {
                      setComboGroupsDraft((prev) => {
                        if (prev?.length) {
                          seedGroupEdits(prev);
                          return prev;
                        }
                        const g = makeEmptyGroup();
                        seedGroupEdits([g]);
                        return [g];
                      });
                    }
                  }
                }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <option value="single">Single item</option>
                <option value="combo">Combo (customizable)</option>
              </select>
            </label>

            {/* ✅ NEW: COMBO MODE SWITCH */}
            {itemTypeDraft === "combo" && (
              <div className="menu-field" onKeyDown={(e) => e.stopPropagation()}>
                <span style={{ display: "block", fontWeight: 700, marginBottom: 8 }}>
                  Combo builder style
                </span>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={`menu-secondary-btn ${comboMode === "groups" ? "active" : ""}`}
                    onClick={() => setComboMode("groups")}
                  >
                    Option groups (Uber-style)
                  </button>

                  <button
                    type="button"
                    className={`menu-secondary-btn ${comboMode === "fixed" ? "active" : ""}`}
                    onClick={() => setComboMode("fixed")}
                  >
                    Fixed includes (legacy)
                  </button>
                </div>

                <div className="menu-microcopy" style={{ marginTop: 8 }}>
                  Use <b>Option groups</b> for “Required: choose 1” sections like Size,
                  Drinks, Desserts. Use <b>Fixed includes</b> only if you want a static list.
                </div>
              </div>
            )}

            {/* ✅ OPTION GROUPS BUILDER */}
            {itemTypeDraft === "combo" && comboMode === "groups" && (
              <div
                className="menu-field"
                style={{ border: "1px solid #eee", padding: 12, borderRadius: 12 }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>Customization groups</div>
                    <div className="menu-microcopy" style={{ marginTop: 4 }}>
                      Example: <b>Size (Required, choose 1)</b>, then <b>Add drinks</b>, <b>Add desserts</b>.
                    </div>
                  </div>
                  <button type="button" className="menu-secondary-btn" onClick={addGroup}>
                    + Add group
                  </button>
                </div>

                <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
                  {comboGroupsDraft.map((g, groupIdx) => {
                    const gEdit = groupEditsRef.current.get(g.id) || {};
                    const titleDefault = gEdit.title ?? g.title ?? "";
                    const optionalDefault = gEdit.optional === true || g.optional === true;
                    const requiredDefault = !optionalDefault;

                    const minDefault =
                      gEdit.minSelect !== undefined ? gEdit.minSelect : g.minSelect ?? (requiredDefault ? 1 : 0);
                    const maxDefault =
                      gEdit.maxSelect !== undefined ? gEdit.maxSelect : g.maxSelect ?? 1;

                    return (
                      <div
                        key={g.id}
                        style={{ border: "1px solid #f0f0f0", borderRadius: 12, padding: 12, background: "#fff" }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: 1 }}>
                            <input
                              style={{ flex: 1, minWidth: 220 }}
                              placeholder={groupIdx === 0 ? "e.g., Size" : "e.g., Add drinks"}
                              defaultValue={titleDefault}
                              onInput={(e) => {
                                const prev = groupEditsRef.current.get(g.id) || {
                                  title: "",
                                  required: true,
                                  optional: false,
                                  minSelect: 1,
                                  maxSelect: 1,
                                };
                                groupEditsRef.current.set(g.id, { ...prev, title: e.currentTarget.value });
                              }}
                              onBlur={(e) => {
                                updateGroup(g.id, { title: e.currentTarget.value });
                              }}
                              onKeyDown={(e) => e.stopPropagation()}
                            />

                            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                type="checkbox"
                                checked={requiredDefault}
                                onChange={(e) => setGroupRequired(g.id, e.target.checked)}
                              />
                              Required
                            </label>

                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span className="menu-microcopy">Min</span>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                style={{ width: 80 }}
                                value={requiredDefault ? minDefault : 0}
                                onChange={(e) => {
                                  const prev = groupEditsRef.current.get(g.id) || {
                                    title: "",
                                    required: true,
                                    optional: false,
                                    minSelect: 1,
                                    maxSelect: 1,
                                  };
                                  const v = Math.max(0, Number.parseInt(e.currentTarget.value, 10) || 0);
                                  groupEditsRef.current.set(g.id, { ...prev, minSelect: v });
                                  updateGroup(g.id, { minSelect: v });
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                                disabled={!requiredDefault}
                              />

                              <span className="menu-microcopy">Max</span>
                              <input
                                type="number"
                                min={1}
                                step={1}
                                style={{ width: 80 }}
                                value={maxDefault}
                                onChange={(e) => {
                                  const prev = groupEditsRef.current.get(g.id) || {
                                    title: "",
                                    required: true,
                                    optional: false,
                                    minSelect: 1,
                                    maxSelect: 1,
                                  };
                                  const v = Math.max(1, Number.parseInt(e.currentTarget.value, 10) || 1);
                                  groupEditsRef.current.set(g.id, { ...prev, maxSelect: v });
                                  updateGroup(g.id, { maxSelect: v });
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>

                          <button type="button" className="menu-link danger" onClick={() => removeGroup(g.id)}>
                            Remove group
                          </button>
                        </div>

                        {/* OPTIONS */}
                        <div style={{ display: "grid", gap: 8 }}>
                          {(g.options || []).map((o, optIdx) => {
                            const key = `${g.id}:${o.id}`;
                            const oEdit = optionEditsRef.current.get(key) || {};
                            const nameDefault = oEdit.name ?? o.name ?? "";
                            const priceDefault =
                              oEdit.priceDelta !== undefined ? oEdit.priceDelta : o.priceDelta ?? 0;

                            return (
                              <div
                                key={o.id}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 140px 90px",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                <input
                                  placeholder={optIdx === 0 ? "Option name (e.g., Footlong)" : "Option name"}
                                  defaultValue={nameDefault}
                                  onInput={(e) => {
                                    const prev = optionEditsRef.current.get(key) || { name: "", priceDelta: 0 };
                                    optionEditsRef.current.set(key, { ...prev, name: e.currentTarget.value });
                                  }}
                                  onBlur={(e) => {
                                    updateOption(g.id, o.id, { name: e.currentTarget.value });
                                  }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                />

                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="+$0.00"
                                  defaultValue={priceDefault}
                                  onInput={(e) => {
                                    const prev = optionEditsRef.current.get(key) || { name: "", priceDelta: 0 };
                                    const v = Number.parseFloat(e.currentTarget.value);
                                    optionEditsRef.current.set(key, { ...prev, priceDelta: Number.isNaN(v) ? 0 : v });
                                  }}
                                  onBlur={(e) => {
                                    const v = Number.parseFloat(e.currentTarget.value);
                                    updateOption(g.id, o.id, { priceDelta: Number.isNaN(v) ? 0 : v });
                                  }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                />

                                <button type="button" className="menu-link danger" onClick={() => removeOption(g.id, o.id)}>
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between" }}>
                          <button type="button" className="menu-secondary-btn" onClick={() => addOption(g.id)}>
                            + Add option
                          </button>

                          <div className="menu-microcopy" style={{ alignSelf: "center" }}>
                            Tip: Use price delta like <b>+5.31</b> for Footlong upgrade.
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* LEGACY: FIXED INCLUDES BUILDER */}
            {itemTypeDraft === "combo" && comboMode === "fixed" && (
              <div className="menu-field" style={{ border: "1px solid #eee", padding: 12, borderRadius: 12 }} onKeyDown={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>Combo includes</div>
                    <div className="menu-microcopy" style={{ marginTop: 4 }}>
                      Example: Burger, Fries, Drink (fixed combo)
                    </div>
                  </div>
                  <button type="button" className="menu-secondary-btn" onClick={addComboRow}>
                    + Add item
                  </button>
                </div>

                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  {comboDraft.map((row, idx) => (
                    <div
                      key={row.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 90px 90px",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <input
                        defaultValue={(comboEditsRef.current.get(row.id)?.name ?? row.name) || ""}
                        onInput={(e) => {
                          const prev = comboEditsRef.current.get(row.id) || { name: "", qty: 1 };
                          comboEditsRef.current.set(row.id, { ...prev, name: e.currentTarget.value });
                        }}
                        onBlur={(e) => {
                          updateComboRow(row.id, { name: e.currentTarget.value });
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder={idx === 0 ? "e.g. Burger" : "e.g. Fries"}
                      />
                      <input
                        type="number"
                        min={1}
                        step={1}
                        defaultValue={
                          comboEditsRef.current.get(row.id)?.qty ??
                          (typeof row.qty === "number" ? row.qty : Number.parseInt(row.qty, 10) || 1)
                        }
                        onInput={(e) => {
                          const prev = comboEditsRef.current.get(row.id) || { name: "", qty: 1 };
                          const qty = Math.max(1, Number.parseInt(e.currentTarget.value, 10) || 1);
                          comboEditsRef.current.set(row.id, { ...prev, qty });
                        }}
                        onBlur={(e) => {
                          const qty = Math.max(1, Number.parseInt(e.currentTarget.value, 10) || 1);
                          updateComboRow(row.id, { qty });
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder="Qty"
                      />
                      <button type="button" className="menu-link danger" onClick={() => removeComboRow(row.id)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ITEM PHOTO */}
            <label className="menu-field">
              <span>Item photo</span>
              <input type="file" name="photo" accept="image/*" onChange={handleItemPhotoPick} />
              <div className="menu-microcopy" style={{ marginTop: 6 }}>
                Choose an image, then crop and optimize it before upload. Output is saved as a square optimized image.
              </div>
              {itemPhotoFile && (
                <div className="menu-image-preview">
                  <img src={itemPhotoPreviewUrl} alt="Selected item preview" />
                  <div className="menu-microcopy" style={{ marginTop: 8 }}>
                    Optimized image ready • {(itemPhotoFile.size / 1024).toFixed(0)} KB
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="menu-link danger" onClick={clearSelectedItemPhoto}>
                      Remove selected image
                    </button>
                  </div>
                </div>
              )}
              {!itemPhotoFile && currentItem && currentItem.imageUrl && (
                <div className="menu-image-preview">
                  <img src={currentItem.imageUrl} alt={currentItem.name || "Current item photo"} />
                  <p className="menu-microcopy">Existing photo – uploading a new optimized image will replace this.</p>
                </div>
              )}
            </label>

            {/* CATEGORY */}
            <label className="menu-field">
              <span>Category</span>
              <input
                list="menu-category-options"
                name="category"
                autoComplete="off"
                defaultValue={currentItem ? currentItem.category : ""}
                placeholder="Type to search or create"
              />
              <datalist id="menu-category-options">
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name} />
                ))}
              </datalist>
            </label>

            <div className="menu-form-grid">
              <label className="menu-field">
                <span>Price</span>
                <input
                  name="price"
                  type="number"
                  step="0.01"
                  defaultValue={currentItem && typeof currentItem.price === "number" ? currentItem.price : ""}
                  placeholder="0.00"
                  required
                />
              </label>

              <label className="menu-field">
                <span>Tax rate %</span>
                <input
                  name="taxRate"
                  type="number"
                  step="0.01"
                  defaultValue={
                    currentItem && currentItem.taxRate !== undefined && currentItem.taxRate !== null
                      ? currentItem.taxRate
                      : ""
                  }
                  placeholder="e.g. 5"
                />
              </label>
            </div>

            {/* STATUS */}
            <label className="menu-field">
              <span>Status</span>
              <select name="status" defaultValue={currentItem && currentItem.status ? currentItem.status : "available"}>
                <option value="available">Available</option>
                <option value="out_of_stock">Out of stock</option>
                <option value="archived">Archived</option>
              </select>
            </label>

            {/* SELL ON ITS OWN */}
            <label className="menu-toggle-field">
              <input type="checkbox" name="sellOnItsOwn" defaultChecked={currentItem ? currentItem.sellOnItsOwn !== false : true} />
              <div>
                <p className="menu-toggle-title">Sell item on its own?</p>
                <p className="menu-toggle-sub">If disabled, this may be add-on only.</p>
              </div>
            </label>

            {/* ACTIONS */}
            <div className="menu-form-actions item-editor-actions">
              {editingId && (
                <button type="button" className="menu-link danger" onClick={handleItemDelete}>
                  Delete
                </button>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button type="button" className="menu-secondary-btn" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="menu-primary-btn" disabled={itemSaving}>
                  {itemSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </form>
        </section>

        {csvImportOpen && (
          <div className="menu-modal" role="dialog" aria-modal="true">
            <div className="menu-modal_content" style={{ maxWidth: 780 }}>
              <div className="menu-card-heading" style={{ alignItems: "flex-start" }}>
                <div>
                  <h3>Import menu from CSV</h3>
                  <p>
                    Adds new items and updates existing items when the CSV row matches by item name and category.
                    Nothing is deleted automatically.
                  </p>
                </div>
                <button type="button" className="menu-link" onClick={closeCsvImport} disabled={csvImportSaving}>
                  Close
                </button>
              </div>

              <div className="menu-alert" style={{ background: "#f8fafc", borderColor: "#e5e7eb", color: "#475569" }}>
                Required columns: <b>name</b>, <b>description</b>, <b>category</b>, <b>price</b>, <b>isAvailable</b>.
                Use <b>yes/no</b> for availability. Existing items are matched by <b>name + category</b>.
              </div>

              <div className="menu-form" style={{ gap: 12 }}>
                <label className="menu-field">
                  <span>Choose CSV file</span>
                  <input
                    ref={csvFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsvFilePick}
                    disabled={csvImportSaving}
                  />
                </label>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" className="menu-secondary-btn" onClick={downloadCsvTemplate}>
                    Download sample CSV
                  </button>
                  <button type="button" className="menu-secondary-btn" onClick={resetCsvImport} disabled={csvImportSaving}>
                    Clear
                  </button>
                </div>

                {csvFileName && (
                  <div className="menu-microcopy">
                    Selected file: <b>{csvFileName}</b>
                  </div>
                )}

                {csvImportMessage && (
                  <div className="menu-alert" style={{ background: "#ecfdf3", borderColor: "#a7f3d0", color: "#027a48" }}>
                    {csvImportMessage}
                  </div>
                )}

                {csvPreviewErrors.length > 0 && (
                  <div className="menu-alert error">
                    <b>Please fix these before importing:</b>
                    <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                      {csvPreviewErrors.slice(0, 12).map((error, idx) => (
                        <li key={`${error}-${idx}`}>{error}</li>
                      ))}
                    </ul>
                    {csvPreviewErrors.length > 12 && (
                      <div style={{ marginTop: 6 }}>
                        Showing first 12 errors out of {csvPreviewErrors.length}.
                      </div>
                    )}
                  </div>
                )}

                {csvPreviewItems.length > 0 && (
                  <div>
                    <div className="menu-card-heading" style={{ marginBottom: 8 }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 16 }}>Preview</h3>
                        <p className="menu-microcopy">
                          {csvPreviewItems.length} items ready. First 10 rows shown below.
                        </p>
                      </div>
                    </div>

                    <div className="menu-table-wrapper" style={{ maxHeight: 320, overflow: "auto" }}>
                      <table className="menu-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreviewItems.slice(0, 10).map((item, idx) => (
                            <tr key={`${item.name}-${idx}`}>
                              <td>{item.name}</td>
                              <td>{item.category}</td>
                              <td>${Number.parseFloat(item.price).toFixed(2)}</td>
                              <td>
                                <span className={`menu-status-pill ${item.isAvailable !== false ? "available" : "unavailable"}`}>
                                  {item.isAvailable !== false ? "Available" : "Out of stock"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="menu-form-actions" style={{ justifyContent: "flex-end" }}>
                  <button type="button" className="menu-secondary-btn" onClick={closeCsvImport} disabled={csvImportSaving}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="menu-primary-btn"
                    onClick={handleCsvImportSave}
                    disabled={
                      csvImportSaving ||
                      csvPreviewItems.length === 0 ||
                      csvPreviewErrors.length > 0
                    }
                  >
                    {csvImportSaving ? "Importing…" : `Import / update ${csvPreviewItems.length || ""} items`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const imageEditorCropSettings =
    imageEditorReady && imageEditorImageRef.current
      ? getSquareCropSettings(
          imageEditorImageRef.current,
          imageEditorZoom,
          imageEditorOffsetX,
          imageEditorOffsetY
        )
      : {
          maxOffsetX: 0,
          maxOffsetY: 0,
        };

  const renderCategoriesTab = () => {
    const uncategorizedCount = categoriesWithItems.uncategorized.length;

    return (
      <div className="menu-layout">
        <section className="menu-card item-editor">
          <div className="menu-card-heading">
            <div>
              <h2 className="menu-section-title">
                {categoryForm.id ? "Edit category" : "New category"}
              </h2>
              <p className="menu-microcopy">
                Create, edit, activate, and order categories for your menu.
              </p>
            </div>
            {categoryForm.id && (
              <button
                type="button"
                className="menu-secondary-btn"
                onClick={resetCategoryFormState}
              >
                Start new
              </button>
            )}
          </div>

          <form className="menu-form" onSubmit={handleCategorySubmit}>
            <label className="menu-field">
              <span>Category name</span>
              <input
                name="name"
                value={categoryForm.name}
                onChange={handleCategoryChange}
                placeholder="e.g. Burgers"
                required
              />
            </label>

            <div className="menu-form-grid">
              <label className="menu-field">
                <span>Sort order</span>
                <input
                  name="sortOrder"
                  type="number"
                  min="1"
                  step="1"
                  value={categoryForm.sortOrder}
                  onChange={handleCategoryChange}
                />
              </label>

              <label className="menu-toggle-field" style={{ marginTop: 26 }}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={categoryForm.isActive}
                  onChange={handleCategoryChange}
                />
                <div>
                  <p className="menu-toggle-title">Active category</p>
                  <p className="menu-toggle-sub">
                    Inactive categories stay saved but can be hidden from use.
                  </p>
                </div>
              </label>
            </div>

            <div className="menu-form-actions item-editor-actions">
              {categoryForm.id && (
                <button
                  type="button"
                  className="menu-link danger"
                  onClick={() => handleCategoryDelete(categoryForm.id)}
                >
                  Delete
                </button>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="menu-secondary-btn"
                  onClick={resetCategoryFormState}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="menu-secondary-btn"
                  disabled={syncingCategories}
                  onClick={handleSyncCategories}
                >
                  {syncingCategories ? "Syncing…" : "Sync from items"}
                </button>
                <button type="submit" className="menu-primary-btn" disabled={categorySaving}>
                  {categorySaving ? "Saving…" : categoryForm.id ? "Update category" : "Save category"}
                </button>
              </div>
            </div>
          </form>
        </section>

        <section className="menu-card">
          <div className="menu-card-heading">
            <div>
              <h2 className="menu-section-title">Categories</h2>
              <p className="menu-microcopy">
                Reorder categories and manage which ones are active.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="menu-secondary-btn"
                onClick={() => {
                  setCategoryOrderDraft(sortCategoriesForOverview(categories));
                  setCategoryOrderDirty(false);
                }}
                disabled={!categories.length}
              >
                Reset order
              </button>
              <button
                type="button"
                className="menu-primary-btn"
                onClick={saveCategoryOrder}
                disabled={!categoryOrderDirty || categoryOrderSaving}
              >
                {categoryOrderSaving ? "Saving…" : "Save order"}
              </button>
            </div>
          </div>

          <div className="menu-microcopy" style={{ marginBottom: 12 }}>
            Uncategorized items: <strong>{uncategorizedCount}</strong>
          </div>

          <div className="menu-table-wrapper">
            <table className="menu-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th>Order</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {categoriesLoading ? (
                  <tr>
                    <td colSpan={5} className="menu-microcopy">Loading categories…</td>
                  </tr>
                ) : categoryOrderDraft.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="menu-microcopy">
                      No categories yet. Save one here or sync them from items.
                    </td>
                  </tr>
                ) : (
                  categoryOrderDraft.map((cat, index) => {
                    const itemCount = getItemCountForCategory(cat);

                    return (
                      <tr key={cat.id}>
                        <td>
                          <div className="menu-item-title">{cat.name || "Untitled"}</div>
                        </td>
                        <td>{itemCount}</td>
                        <td>
                          <span className={`menu-status-pill ${cat.isActive ? "available" : "unavailable"}`}>
                            {cat.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              type="button"
                              className="menu-secondary-btn"
                              onClick={() => moveCategory(index, -1)}
                              disabled={index === 0}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="menu-secondary-btn"
                              onClick={() => moveCategory(index, 1)}
                              disabled={index === categoryOrderDraft.length - 1}
                            >
                              ↓
                            </button>
                          </div>
                        </td>
                        <td className="menu-row-actions">
                          <button
                            type="button"
                            className="menu-link"
                            onClick={() => startCategoryEdit(cat)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="menu-link danger"
                            onClick={() => handleCategoryDelete(cat.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  };

  return (
    <ManagerShell title="Menu manager" subtitle="Manage your menu items and categories.">
      <div className="menu-shell">
        <div className="menu-tabs">
          {NAV_TABS.map((t) => (
            <button
              key={t}
              type="button"
              className={`menu-tab ${activeTab === t ? "active" : ""}`}
              onClick={() => navigate(`/menu/${t}`)}
            >
              {t === "overview" ? "Overview" : t === "items" ? "Items" : "Categories"}
            </button>
          ))}
        </div>

        {syncSummary && <div className="menu-alert success">{syncSummary}</div>}
        {categoriesError && <div className="menu-alert error">{categoriesError}</div>}

        {activeTab === "overview" && renderOverviewTab()}
        {activeTab === "items" && renderItemsTab()}
        {activeTab === "categories" && renderCategoriesTab()}
      </div>

      {imageEditorOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(15, 23, 42, 0.68)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              width: "min(960px, 100%)",
              maxHeight: "90vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: 20,
              padding: 20,
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Optimize item image</h3>
                <p style={{ margin: "6px 0 0", color: "#64748b", lineHeight: 1.6 }}>
                  Adjust zoom and position, then save an optimized square image for this menu item.
                </p>
              </div>
              <button type="button" className="menu-secondary-btn" onClick={closeImageEditor}>
                Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(280px, 360px) minmax(280px, 1fr)",
                gap: 20,
                alignItems: "start",
              }}
            >
              <div>
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: 18,
                    overflow: "hidden",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    background: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <canvas
                    ref={imageEditorCanvasRef}
                    style={{ width: "100%", height: "100%", display: "block" }}
                  />
                </div>
                <div className="menu-microcopy" style={{ marginTop: 10 }}>
                  Final upload: square image • {imageEditorOutputSize}px • {imageEditorQuality}% quality
                </div>
              </div>

              <div style={{ display: "grid", gap: 14 }}>
                <label className="menu-field" style={{ gap: 8 }}>
                  <span>Zoom</span>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={imageEditorZoom}
                    onChange={(e) => setImageEditorZoom(Number.parseFloat(e.target.value) || 1)}
                  />
                  <div className="menu-microcopy">{imageEditorZoom.toFixed(2)}×</div>
                </label>

                <label className="menu-field" style={{ gap: 8 }}>
                  <span>Horizontal position</span>
                  <input
                    type="range"
                    min={-Math.round(imageEditorCropSettings.maxOffsetX)}
                    max={Math.round(imageEditorCropSettings.maxOffsetX)}
                    step="1"
                    value={imageEditorOffsetX}
                    onChange={(e) => setImageEditorOffsetX(Number.parseInt(e.target.value, 10) || 0)}
                    disabled={!imageEditorReady || imageEditorCropSettings.maxOffsetX === 0}
                  />
                </label>

                <label className="menu-field" style={{ gap: 8 }}>
                  <span>Vertical position</span>
                  <input
                    type="range"
                    min={-Math.round(imageEditorCropSettings.maxOffsetY)}
                    max={Math.round(imageEditorCropSettings.maxOffsetY)}
                    step="1"
                    value={imageEditorOffsetY}
                    onChange={(e) => setImageEditorOffsetY(Number.parseInt(e.target.value, 10) || 0)}
                    disabled={!imageEditorReady || imageEditorCropSettings.maxOffsetY === 0}
                  />
                </label>

                <label className="menu-field" style={{ gap: 8 }}>
                  <span>Output size</span>
                  <select
                    value={imageEditorOutputSize}
                    onChange={(e) =>
                      setImageEditorOutputSize(Number.parseInt(e.target.value, 10) || MENU_IMAGE_EDITOR_DEFAULT_OUTPUT_SIZE)
                    }
                  >
                    <option value={800}>800 × 800</option>
                    <option value={1200}>1200 × 1200</option>
                    <option value={1600}>1600 × 1600</option>
                  </select>
                </label>

                <label className="menu-field" style={{ gap: 8 }}>
                  <span>Quality</span>
                  <input
                    type="range"
                    min="60"
                    max="95"
                    step="1"
                    value={imageEditorQuality}
                    onChange={(e) => setImageEditorQuality(Number.parseInt(e.target.value, 10) || 82)}
                  />
                  <div className="menu-microcopy">{imageEditorQuality}%</div>
                </label>

                <div className="menu-form-actions" style={{ justifyContent: "flex-end", marginTop: 4 }}>
                  <button type="button" className="menu-secondary-btn" onClick={closeImageEditor}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="menu-primary-btn"
                    onClick={applyOptimizedItemPhoto}
                    disabled={imageEditorBusy || !imageEditorReady}
                  >
                    {imageEditorBusy ? "Optimizing…" : "Use optimized image"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ManagerShell>
  );
};

export default MenuManager;
